import * as React from 'react';
import {
  Star,
  Clock,
  Folder,
  Search,
  X,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { ProjectWithMetadata, ProjectFilters, ProjectStatus, TeamFilter } from '@/types/project-selector';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ProjectSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: ProjectWithMetadata[];
  value?: string;
  onSelect: (projectId: string) => void;
  onToggleFavourite: (projectId: string) => void;
  isFavourite: (projectId: string) => boolean;
}

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export function ProjectSearchModal({
  open,
  onOpenChange,
  projects,
  value,
  onSelect,
  onToggleFavourite,
  isFavourite,
}: ProjectSearchModalProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [filters, setFilters] = React.useState<ProjectFilters>({
    status: 'active',
    team: 'all',
  });
  const [allProjectsPage, setAllProjectsPage] = React.useState(1);
  const [favouritesExpanded, setFavouritesExpanded] = React.useState(true);
  const [recentExpanded, setRecentExpanded] = React.useState(true);
  const [allProjectsExpanded, setAllProjectsExpanded] = React.useState(true);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setFilters({ status: 'active', team: 'all' });
      setAllProjectsPage(1);
      setFocusedIndex(-1);
    }
  }, [open]);

  // Fuzzy search implementation
  const fuzzyMatch = React.useCallback((text: string, query: string): boolean => {
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    
    // Exact match
    if (textLower.includes(queryLower)) return true;
    
    // Fuzzy match - allow for typos
    let queryIndex = 0;
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        queryIndex++;
      }
    }
    return queryIndex === queryLower.length;
  }, []);

  // Filter and search projects
  const filteredProjects = React.useMemo(() => {
    let filtered = projects;

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter((p) => p.status === filters.status);
    }

    // Apply team filter (placeholder - would need actual team data)
    // For now, just filter by team size as a proxy
    if (filters.team === 'my-projects') {
      filtered = filtered.filter((p) => p.teamSize <= 5);
    } else if (filters.team === 'team-projects') {
      filtered = filtered.filter((p) => p.teamSize > 5);
    }

    // Apply search
    if (debouncedSearch) {
      filtered = filtered.filter((p) => {
        return (
          fuzzyMatch(p.name, debouncedSearch) ||
          fuzzyMatch(p.code, debouncedSearch) ||
          fuzzyMatch(p.clientName, debouncedSearch) ||
          (p.description && fuzzyMatch(p.description, debouncedSearch))
        );
      });
    }

    return filtered;
  }, [projects, filters, debouncedSearch, fuzzyMatch]);

  // Categorize filtered projects
  const categorizedProjects = React.useMemo(() => {
    const favourites = filteredProjects
      .filter((p) => isFavourite(p.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    const recent = filteredProjects
      .filter((p) => p.lastAccessedDate && !isFavourite(p.id))
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, 10);

    const recentIds = new Set(recent.map((p) => p.id));
    const favouriteIds = new Set(favourites.map((p) => p.id));

    const all = filteredProjects
      .filter((p) => !favouriteIds.has(p.id) && !recentIds.has(p.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { favourites, recent, all };
  }, [filteredProjects, isFavourite]);

  // Paginate all projects
  const paginatedAllProjects = React.useMemo(() => {
    return categorizedProjects.all.slice(0, allProjectsPage * ITEMS_PER_PAGE);
  }, [categorizedProjects.all, allProjectsPage]);

  const hasMoreProjects = paginatedAllProjects.length < categorizedProjects.all.length;

  // Handle star toggle with animation
  const handleToggleFavourite = React.useCallback(
    (e: React.MouseEvent, projectId: string) => {
      e.stopPropagation();
      onToggleFavourite(projectId);
      
      // Announce to screen readers
      const project = projects.find((p) => p.id === projectId);
      const message = isFavourite(projectId)
        ? `${project?.name} removed from favourites`
        : `${project?.name} added to favourites`;
      
      // Create a temporary announcement element
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = message;
      document.body.appendChild(announcement);
      setTimeout(() => document.body.removeChild(announcement), 1000);
    },
    [onToggleFavourite, isFavourite, projects]
  );

  // Handle project selection
  const handleSelect = React.useCallback(
    (projectId: string) => {
      onSelect(projectId);
      onOpenChange(false);
    },
    [onSelect, onOpenChange]
  );

  // Clear filters
  const handleClearFilters = React.useCallback(() => {
    setFilters({ status: 'active', team: 'all' });
  }, []);

  const hasActiveFilters = filters.status !== 'active' || filters.team !== 'all';

  // Keyboard navigation
  const allVisibleProjects = React.useMemo(() => {
    const visible: ProjectWithMetadata[] = [];
    if (favouritesExpanded) visible.push(...categorizedProjects.favourites);
    if (recentExpanded) visible.push(...categorizedProjects.recent);
    if (allProjectsExpanded) visible.push(...paginatedAllProjects);
    return visible;
  }, [
    favouritesExpanded,
    recentExpanded,
    allProjectsExpanded,
    categorizedProjects,
    paginatedAllProjects,
  ]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < allVisibleProjects.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        e.preventDefault();
        handleSelect(allVisibleProjects[focusedIndex].id);
      } else if (e.key === ' ' && focusedIndex >= 0) {
        e.preventDefault();
        handleToggleFavourite(e as any, allVisibleProjects[focusedIndex].id);
      }
    },
    [allVisibleProjects, focusedIndex, handleSelect, handleToggleFavourite]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[600px] h-[70vh] max-h-[700px] flex flex-col p-0"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle>Select Project</DialogTitle>
        </DialogHeader>

        {/* Search and Filters */}
        <div className="px-6 py-4 space-y-3 border-b">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium">Filters:</span>
            <Select
              value={filters.status}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, status: value as ProjectStatus | 'all' }))
              }
            >
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.team}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, team: value as TeamFilter }))
              }
            >
              <SelectTrigger className="w-[160px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                <SelectItem value="my-projects">My Projects</SelectItem>
                <SelectItem value="team-projects">Team Projects</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-8 text-xs"
              >
                Clear Filters
              </Button>
            )}

            {hasActiveFilters && (
              <Badge variant="secondary" className="text-xs">
                Filters ({Object.values(filters).filter((v) => v !== 'all' && v !== 'active').length})
              </Badge>
            )}
          </div>

          {/* Results count */}
          <div className="text-xs text-muted-foreground" role="status" aria-live="polite">
            Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Results */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full px-6">
            <div className="py-4 space-y-4">
              {/* Favourites Section */}
              {categorizedProjects.favourites.length > 0 && (
                <Collapsible open={favouritesExpanded} onOpenChange={setFavouritesExpanded}>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-2 hover:bg-accent/50 rounded-md px-2 transition-colors">
                    {favouritesExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <Star className="h-4 w-4" />
                    <span className="text-sm font-bold uppercase">Favourites</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {categorizedProjects.favourites.length}
                    </Badge>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-1">
                    {categorizedProjects.favourites.map((project, index) => (
                      <ProjectItem
                        key={project.id}
                        project={project}
                        isSelected={value === project.id}
                        isFocused={focusedIndex === index}
                        onSelect={handleSelect}
                        onToggleFavourite={handleToggleFavourite}
                        isFavourite={isFavourite(project.id)}
                      />
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Recently Used Section */}
              {categorizedProjects.recent.length > 0 && (
                <Collapsible open={recentExpanded} onOpenChange={setRecentExpanded}>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-2 hover:bg-accent/50 rounded-md px-2 transition-colors">
                    {recentExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-bold uppercase">Recently Used</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {categorizedProjects.recent.length}
                    </Badge>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 space-y-1">
                    {categorizedProjects.recent.map((project, index) => {
                      const globalIndex = categorizedProjects.favourites.length + index;
                      return (
                        <ProjectItem
                          key={project.id}
                          project={project}
                          isSelected={value === project.id}
                          isFocused={focusedIndex === globalIndex}
                          onSelect={handleSelect}
                          onToggleFavourite={handleToggleFavourite}
                          isFavourite={isFavourite(project.id)}
                        />
                      );
                    })}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* All Projects Section */}
              <Collapsible open={allProjectsExpanded} onOpenChange={setAllProjectsExpanded}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-2 hover:bg-accent/50 rounded-md px-2 transition-colors">
                  {allProjectsExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <Folder className="h-4 w-4" />
                  <span className="text-sm font-bold uppercase">All Projects</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {categorizedProjects.all.length}
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-1">
                  {paginatedAllProjects.length === 0 ? (
                    <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                      {debouncedSearch
                        ? `No projects found matching "${debouncedSearch}". Try adjusting your filters.`
                        : 'No projects available. Contact your administrator.'}
                    </div>
                  ) : (
                    <>
                      {paginatedAllProjects.map((project, index) => {
                        const globalIndex =
                          categorizedProjects.favourites.length +
                          categorizedProjects.recent.length +
                          index;
                        return (
                          <ProjectItem
                            key={project.id}
                            project={project}
                            isSelected={value === project.id}
                            isFocused={focusedIndex === globalIndex}
                            onSelect={handleSelect}
                            onToggleFavourite={handleToggleFavourite}
                            isFavourite={isFavourite(project.id)}
                          />
                        );
                      })}
                      {hasMoreProjects && (
                        <Button
                          variant="ghost"
                          className="w-full mt-2"
                          onClick={() => setAllProjectsPage((prev) => prev + 1)}
                        >
                          Load More ({categorizedProjects.all.length - paginatedAllProjects.length}{' '}
                          remaining)
                        </Button>
                      )}
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical">
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// Project Item Component
interface ProjectItemProps {
  project: ProjectWithMetadata;
  isSelected: boolean;
  isFocused: boolean;
  onSelect: (projectId: string) => void;
  onToggleFavourite: (e: React.MouseEvent, projectId: string) => void;
  isFavourite: boolean;
}

function ProjectItem({
  project,
  isSelected,
  isFocused,
  onSelect,
  onToggleFavourite,
  isFavourite,
}: ProjectItemProps) {
  const [showArrow, setShowArrow] = React.useState(false);

  return (
    <button
      onClick={() => onSelect(project.id)}
      onMouseEnter={() => setShowArrow(true)}
      onMouseLeave={() => setShowArrow(false)}
      className={cn(
        'w-full flex items-center gap-3 px-2 py-2.5 text-sm rounded-md transition-all text-left group',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected && 'bg-accent',
        isFocused && 'ring-2 ring-ring'
      )}
      aria-selected={isSelected}
      role="option"
    >
      {/* Star Icon */}
      <button
        onClick={(e) => onToggleFavourite(e, project.id)}
        className={cn(
          'shrink-0 p-0.5 rounded hover:bg-accent-foreground/10 transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
        aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
        tabIndex={-1}
      >
        {isFavourite ? (
          <Star className="h-5 w-5 fill-yellow-400 text-yellow-400 animate-in zoom-in-50 duration-200" />
        ) : (
          <Star className="h-5 w-5 text-muted-foreground hover:text-yellow-400 transition-colors" />
        )}
      </button>

      {/* Project Info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{project.name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {project.code} • {project.clientName}
        </div>
      </div>

      {/* Status Badge */}
      {project.status !== 'active' && (
        <Badge
          variant={project.status === 'archived' ? 'destructive' : 'secondary'}
          className="text-xs shrink-0"
        >
          {project.status}
        </Badge>
      )}

      {/* Arrow Icon */}
      <ArrowRight
        className={cn(
          'h-4 w-4 text-muted-foreground shrink-0 transition-all',
          (showArrow || isFocused) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
        )}
      />
    </button>
  );
}
